"use client";

import * as React from "react";
import { useProfileGuard } from "@/frontend/hooks/use-profile-guard";
import { Spinner } from "@components/ui/spinner";
import { ErrorState } from "@components/ui/error-state";
import { useTranslations } from "next-intl";
import { useSetAtom } from "jotai";
import { profileLoadingStatusAtom } from "@lib/atoms";

/**
 * ProfileGuard component that wraps the app and ensures users have completed their profile
 * Redirects to onboarding if profile is missing
 */
export function ProfileGuard({ children }: { children: React.ReactNode }) {
  const { isLoading, hasError, needsOnboarding } = useProfileGuard();
  const setProfileLoadingStatus = useSetAtom(profileLoadingStatusAtom);
  const t = useTranslations("profileGuard");
  const tError = useTranslations("errorState");

  const handleRetry = () => {
    // Reset status to 'init' to trigger profile reload in useProfileGuard
    setProfileLoadingStatus("init");
  };

  // Show error state if profile loading failed
  if (hasError) {
    return (
      <ErrorState
        title={tError("title")}
        description={tError("description")}
        retryButtonText={tError("retryButton")}
        contactSupportText={tError("persistentProblem")}
        contactSupportLabel={tError("contactSupport")}
        onRetry={handleRetry}
      />
    );
  }

  // Show loading state while checking profile
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-muted-foreground text-sm">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // If user needs onboarding, the hook will redirect
  // Show loading state during redirect
  if (needsOnboarding) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-muted-foreground text-sm">{t("redirecting")}</p>
        </div>
      </div>
    );
  }

  // Profile complete or on onboarding page, render children
  return <>{children}</>;
}
